/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ApiService from "../services/apiServices";
import { getFromLocalStorage } from "../utils/storage";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// No hay valor por defecto a propósito: un build mal configurado debe fallar de
// forma evidente en lugar de apuntar en silencio a producción desde el panel de
// pruebas. En local sale de .env.local; para desplegar, `npm run build:pruebas`
// o `npm run build:produccion` (ver .env.pruebas y .env.produccion).
const BASE_PATH_HTTP =
  process.env.REACT_APP_API_URL;

// Endpoint que recibe los archivos grandes por partes. Un ZIP de fotos no cabe
// en una sola petición: el CDN corta el cuerpo en 512 MB y PHP en 256 MB.
const CHUNK_UPLOAD_PATH = "/panel/uploads/chunk";

// La importación tampoco cabe en una sola petición, pero por otro motivo:
// descomprimir el ZIP y reescribir cada foto tarda minutos y el proxy corta la
// espera con un 504. Por eso el trabajo se pide de a pasos.
const IMPORT_START_PATH = "/panel/trees/import/start";
const IMPORT_STEP_PATH = "/panel/trees/import/step";

// Si el servidor deja de avanzar, no tiene sentido seguir pidiendo pasos.
const PASOS_SIN_AVANCE_MAXIMO = 3;

// Cuántas veces se reintenta un paso que falló.
//
// Reintentar es seguro por cómo está armado el proceso: el servidor solo anota
// el avance cuando el paso termina bien, y todo lo que escribe es idempotente
// (las fotos se extraen con el mismo nombre, las filas van por updateOrCreate).
// Un paso que muere se repite sin duplicar nada, así que una caída suelta a
// mitad de una importación de media hora ya no obliga a empezar de cero.
const REINTENTOS_POR_PASO = 4;

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Pide un paso de la importación, reintentando si el servidor falla.
 *
 * Un 4xx no se reintenta: es el servidor rechazando la petición y daría lo
 * mismo la segunda vez. Sí se reintenta lo demás (conexión cortada, 5xx), que
 * es justo lo que produce un límite de recursos alcanzado a mitad del proceso.
 */
const ejecutarPaso = async (apiService, jobId, informar) => {
  for (let intento = 1; ; intento += 1) {
    try {
      return await apiService.postJson(`${BASE_PATH_HTTP}${IMPORT_STEP_PATH}`, {
        jobId,
      });
    } catch (error) {
      const delServidor = error?.status >= 400 && error?.status < 500;

      if (delServidor || intento >= REINTENTOS_POR_PASO) {
        throw error;
      }

      informar(
        "reintentando",
        null,
        `El servidor no respondió. Reintentando (${intento} de ${
          REINTENTOS_POR_PASO - 1
        })...`
      );

      // Espera creciente: si el servidor está saturado, insistir de inmediato
      // solo empeora las cosas.
      await esperar(3000 * intento);
    }
  }
};

const textoDeFase = (estado) => {
  if (estado.fase === "fotos") {
    return `Optimizando fotos... ${estado.fotos.hechas} de ${estado.fotos.total}`;
  }

  if (estado.fase === "filas") {
    return `Actualizando árboles... ${estado.filas.hechas} de ${estado.filas.total}`;
  }

  return "Importación completada.";
};

export const useDataFetch = () => {
  const apiService = new ApiService();
  const queryClient = useQueryClient();

  const user = getFromLocalStorage("user") || {};

  const authHeaders = {
    Authorization: `Bearer ${user?.jwt || ""}`,
    "Content-Type": "application/json",
  };

  // Usa el token del usuario autenticado. Antes había un JWT fijo en el código
  // que expiró el 04/01/2026 y hacía fallar todas las peticiones con 401.
  apiService.setHeaders(user?.jwt);

  const checkIsAuth = () => !!user?.jwt;

  // Http Request
  const getData = (queryKey, url, options = {}, params = {}, headers = {}) => {
    const newHeaders = checkIsAuth() ? { ...headers, ...authHeaders } : headers;

    return useQuery(
      queryKey,
      async () => {
        return await apiService.get(
          `${BASE_PATH_HTTP}${url}`,
          params,
          newHeaders
        );
      },
      {
        retry: 2,
        enabled: false,
        refetchOnWindowFocus: false,
        onError: (error) => {
          console.error("Query Error", error);
        },
        ...options,
      }
    );
  };

  const postData = (url, headers = {}, extraBody = {}, options = {}) => {
    const newHeaders = checkIsAuth() ? { ...headers, ...authHeaders } : headers;

    return useMutation(
      async (body = {}) => {
        return await apiService.post(
          `${BASE_PATH_HTTP}${url}`,
          { ...body, ...extraBody },
          newHeaders
        );
      },
      {
        onSuccess: (data) => {
          toast.success("Se completo la acción correctamente.");
        },
        onError: (error) => {
          console.log(error);
        },
        ...options,
      }
    );
  };

  // `options.onUploadProgress` recibe { loaded, total, percent } durante la
  // subida. La autorización va en apiService.setHeaders(), no por aquí.
  const postMultipart = (url, headers = {}, options = {}) => {
    const { onUploadProgress, ...mutationOptions } = options;

    return useMutation(
      async (formData) => {
        return await apiService.postMultipart(
          `${BASE_PATH_HTTP}${url}`,
          formData,
          onUploadProgress,
          { chunkUrl: `${BASE_PATH_HTTP}${CHUNK_UPLOAD_PATH}` }
        );
      },
      {
        onSuccess: (data) => {
          toast.success("Se completo la acción correctamente.");
        },
        onError: (error) => {
          console.error("Error en multipart:", error);
        },
        ...mutationOptions,
      }
    );
  };

  /**
   * Importación de árboles desde Excel + ZIP.
   *
   * Son dos problemas distintos resueltos por separado: el ZIP no cabe en una
   * petición (lo parte postMultipart) y el procesado no cabe en el tiempo que
   * el proxy espera (lo parten los pasos). `options.onProgreso` recibe
   * { fase, porcentaje, texto } durante todo el recorrido.
   */
  const postImport = (options = {}) => {
    const { onProgreso, ...mutationOptions } = options;

    return useMutation(
      async ({ tipo, excel, zip }) => {
        // El porcentaje se recuerda entre avisos: durante un reintento cambia
        // el texto pero no hay avance nuevo, y la barra no debe volver a cero.
        let ultimoPorcentaje = 0;

        const informar = (fase, porcentaje, texto) => {
          if (porcentaje !== null && porcentaje !== undefined) {
            ultimoPorcentaje = porcentaje;
          }

          if (typeof onProgreso === "function") {
            onProgreso({ fase, porcentaje: ultimoPorcentaje, texto });
          }
        };

        informar("subiendo", 0, "Preparando el envío...");

        const formData = new FormData();
        formData.append("tipo", tipo);
        formData.append("excel", excel);
        if (zip) formData.append("zip", zip);

        // La subida ocupa la primera mitad de la barra y el procesado la
        // segunda: las dos etapas tardan minutos con un ZIP grande.
        const inicio = await apiService.postMultipart(
          `${BASE_PATH_HTTP}${IMPORT_START_PATH}`,
          formData,
          ({ percent }) =>
            informar(
              "subiendo",
              Math.round((percent ?? 0) / 2),
              `Subiendo archivos... ${percent ?? 0}%`
            ),
          { chunkUrl: `${BASE_PATH_HTTP}${CHUNK_UPLOAD_PATH}` }
        );

        let estado;
        let avanceAnterior = -1;
        let sinAvance = 0;

        do {
          estado = await ejecutarPaso(apiService, inicio.jobId, informar);

          const avance = estado.fotos.hechas + estado.filas.hechas;
          sinAvance = avance > avanceAnterior ? 0 : sinAvance + 1;
          avanceAnterior = avance;

          if (sinAvance >= PASOS_SIN_AVANCE_MAXIMO) {
            throw new Error(
              "La importación dejó de avanzar en el servidor. " +
                "Revisa el listado antes de volver a intentarlo."
            );
          }

          // Queda en la consola para poder diagnosticar un corte del servidor
          // sin tener que entrar a mirar sus registros.
          if (estado.memoria) {
            console.info(
              `[importación] ${estado.fase} ${estado.porcentaje}% — memoria ` +
                `${Math.round(estado.memoria.pico / 1048576)} MB de ${estado.memoria.limite}`
            );
          }

          informar(
            estado.fase,
            50 + Math.round(estado.porcentaje / 2),
            textoDeFase(estado)
          );
        } while (!estado.terminado);

        return { trees: estado.trees };
      },
      mutationOptions
    );
  };

  const deleteData = (url, headers = {}, options = {}) => {
    const newHeaders = checkIsAuth() ? { ...headers, ...authHeaders } : headers;

    return useMutation(
      (body = {}) =>
        Swal.fire({
          title: "Confirmación",
          text: "¿Desea eliminar el registro?",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Si",
        }).then((result) => {
          if (result.isConfirmed) {
            apiService.delete(
              `${BASE_PATH_HTTP}${url}${body.id ? "/" + body.id : ""}`,
              newHeaders
            );
          }
        }),
      {
        onSuccess: (data) => {
          toast.success("Se completo la acción correctamente.");
        },
        onError: (error) => {
          console.log(error);
        },
        ...options,
      }
    );
  };

  const patchData = (url, headers = {}, options = {}) => {
    const newHeaders = checkIsAuth() ? { ...headers, ...authHeaders } : headers;

    return useMutation(
      (body = {}) =>
        apiService.put(
          `${BASE_PATH_HTTP}${url}${body.id ? "/" + body.id : ""}`,
          { ...body },
          newHeaders
        ),
      {
        onSuccess: (data) => {
          toast.success("Se completo la acción correctamente.");
        },
        onError: (error) => {
          console.log(error);
        },
        ...options,
      }
    );
  };

  const invalidateQuery = (queryKey) => {
    queryClient.invalidateQueries(queryKey);
  };

  /**
   * Envía JSON y trata como error cualquier respuesta que no sea 2xx, con el
   * mensaje que devuelve el API.
   *
   * postData() entrega el cuerpo sin mirar el código y deleteData() no espera
   * al servidor, así que un 409 («ese tipo ya existe», «está en uso») se veía
   * como éxito. Va aparte para no cambiar las pantallas que ya los usan.
   *
   * method: "POST" | "PUT" | "DELETE". Si el cuerpo trae `id`, se agrega a la
   * URL (/recurso/:id) y no se envía en el cuerpo.
   */
  const sendJson = (method, url, options = {}) =>
    useMutation(
      async ({ id, ...body } = {}) => {
        const destino = `${BASE_PATH_HTTP}${url}${id ? "/" + id : ""}`;

        if (method === "DELETE") return apiService.delete(destino);
        if (method === "PUT") return apiService.put(destino, body);
        return apiService.postJson(destino, body);
      },
      {
        onError: (error) =>
          toast.error(error?.message || "No fue posible completar la acción."),
        ...options,
      }
    );

  return {
    getData,
    postData,
    postMultipart,
    postImport,
    deleteData,
    patchData,
    sendJson,
    invalidateQuery,
  };
};
